import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { getTeamLogoUrl } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import './Leagues.css';

/**
 * Inbox of the logged-in player's pending league-team invitations.
 *
 * Rosters are consent-based: a captain invites a player and the player accepts
 * here before joining. Renders nothing when the user is logged out or has no
 * pending invites, so it can be dropped unconditionally at the top of the teams
 * hub. Accepting surfaces any eligibility error (e.g. already on another active
 * team of that mode) inline and leaves the invite in place to resolve.
 *
 * @param {object} props - Component props.
 * @param {Function} [props.onChange] - Called after an invite is accepted so a
 *   parent (the teams list) can refresh its roster counts.
 * @returns {JSX.Element|null} The invite cards, or null when there's nothing to show.
 */
export default function MyTeamInvites({ onChange }) {
  const { t } = useTranslation();
  const { getAuthHeaders, isLoggedIn } = useAuth();
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!isLoggedIn) {
      setInvites([]);
      return Promise.resolve();
    }
    return fetch(`${API_URL}/api/leagues/my-invites`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setInvites(Array.isArray(data) ? data : []))
      .catch(() => setInvites([]));
  }, [isLoggedIn, getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const respond = async (invite, action) => {
    setBusy(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/api/leagues/invites/${invite.id}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setError(data.error || t('auth.genericError'));
        return;
      }
      await load();
      if (action === 'accept' && onChange) onChange();
    } finally {
      setBusy(false);
    }
  };

  if (!isLoggedIn || invites.length === 0) return null;

  return (
    <div className="team-card notched-card invite-inbox">
      <h3 className="division-title">{t('leagues.invitesCount', { count: invites.length })}</h3>
      {error && <p className="form-error invite-error">{error}</p>}
      <ul className="invite-list">
        {invites.map((inv) => {
          const logo = getTeamLogoUrl(inv.team?.logo_url);
          return (
            <li key={inv.id} className="invite-row">
              {logo ? (
                <img className="team-logo" src={logo} alt="" />
              ) : (
                <span className="team-logo team-logo-blank">
                  {(inv.team?.tag || inv.team?.name || '?')[0]}
                </span>
              )}
              <span className="invite-team">
                <span className="invite-team-name">{inv.team?.name}</span>
                <span className="invite-meta">
                  {t('leagues.inviteAsRole', { role: inv.role_display?.toLowerCase() })}
                  {inv.invited_by_name ? ` ${t('leagues.inviteByName', { name: inv.invited_by_name })}` : ''}
                </span>
                {inv.message && <span className="invite-message">“{inv.message}”</span>}
              </span>
              <span className="invite-actions">
                <button
                  className="form-submit form-submit-sm"
                  disabled={busy}
                  onClick={() => respond(inv, 'accept')}
                >
                  {t('queue.accept')}
                </button>
                <button
                  className="roster-btn roster-btn-danger"
                  title={t('leagues.declineInvite')}
                  disabled={busy}
                  onClick={() => respond(inv, 'decline')}
                >
                  ×
                </button>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
